import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Eye, EyeOff, ExternalLink, ImageOff } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { http, apiError, DIVISIONS, STATUSES, SOURCES, divisionOf, formatPrice, imageUrl } from "../../lib/api";
import { StatusBadge } from "../../components/site/AssetCard";
import { Confirm } from "./Confirm";
import { useAuth } from "../../lib/auth";

export default function AssetsAdmin() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-assets"], queryFn: async () => (await http.get("/admin/assets")).data });
  const [div, setDiv] = useState("");
  const [q, setQ] = useState("");
  const items = useMemo(() => (data?.items || []).filter((a) => (!div || a.division === div) &&
    (!q || `${a.title} ${a.stock_number} ${a.make} ${a.model}`.toLowerCase().includes(q.toLowerCase()))), [data, div, q]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-assets"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };
  const patch = async (a, body, msg) => {
    try {
      await http.patch(`/admin/assets/${a.id}`, body);
      toast.success(msg);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const remove = async (a) => {
    try {
      await http.delete(`/admin/assets/${a.id}`);
      toast.success("Listing deleted");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-inventory">
      <AdminTitle title="Inventory" sub="Only listings Scott has verified can be published. Sold listings are removed from public inventory automatically.">
        <Link to="/admin/inventory/new" className="btn-gold" data-testid="add-asset-button"><Plus className="w-4 h-4" /> Add Asset</Link>
      </AdminTitle>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input className="field sm:max-w-xs !py-2.5" placeholder="Search title, stock #, make…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-asset-search" />
        <select className="field sm:max-w-[220px] !py-2.5" value={div} onChange={(e) => setDiv(e.target.value)} data-testid="admin-asset-division-filter">
          <option value="">All divisions</option>
          {DIVISIONS.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
        </select>
      </div>
      {isLoading ? <p className="text-slate-400">Loading…</p> : !items.length ? (
        <div className="border border-dashed border-white/20 p-12 text-center" data-testid="admin-inventory-empty">
          <p className="font-display text-3xl">No listings yet</p>
          <p className="text-slate-300 mt-2 text-sm">Until you publish listings, visitors see the sourcing message for each division.</p>
          <Link to="/admin/inventory/new" className="btn-gold mt-6">Add your first asset</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="border hairline bg-[var(--ink-2)] p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-asset-row-${a.id}`}>
              <div className="w-full md:w-28 aspect-[4/3] shrink-0 bg-[var(--ink-3)] overflow-hidden">
                {a.images?.[0] ? <img src={imageUrl(a.images[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-500"><ImageOff className="w-5 h-5" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={a.status} />
                  <span className={`font-mono text-[0.65rem] uppercase px-2 py-1 border ${a.published ? "border-emerald-400/40 text-emerald-300" : "border-slate-500/50 text-slate-300"}`}>{a.published ? "Published" : "Draft"}</span>
                  {a.featured && <span className="font-mono text-[0.65rem] uppercase px-2 py-1 border border-[var(--gold)] text-gold">Featured</span>}
                  <span className={`font-mono text-[0.65rem] uppercase px-2 py-1 border ${a.verified ? "border-emerald-400/40 text-emerald-300" : "border-amber-400/50 text-amber-200"}`} data-testid={`asset-verified-badge-${a.id}`}>{a.verified ? "Verified" : "Needs verification"}</span>
                </div>
                <p className="font-display text-2xl mt-2 truncate">{a.title}</p>
                <p className="font-mono text-xs text-slate-400 mt-1">{a.stock_number} · {divisionOf(a.division)?.short} · {a.category || "—"} · {formatPrice(a.price)}{a.source && a.source !== "unspecified" ? ` · ${SOURCES[a.source]}` : ""}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select className="field !w-auto !py-2 !text-xs" value={a.status} onChange={(e) => patch(a, { status: e.target.value }, "Status updated")} data-testid={`asset-status-select-${a.id}`}>
                  {Object.entries(STATUSES).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                </select>
                <IconBtn title={a.verified ? (a.published ? "Unpublish" : "Publish") : "Verify the asset before publishing"} disabled={!a.verified && !a.published} onClick={() => patch(a, { published: !a.published }, a.published ? "Unpublished" : "Published")} testid={`asset-publish-toggle-${a.id}`}>{a.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</IconBtn>
                <IconBtn title={a.featured ? "Remove from homepage" : "Feature on homepage"} active={a.featured} disabled={a.status === "sold" || !a.verified} onClick={() => patch(a, { featured: !a.featured }, a.featured ? "Removed from homepage" : "Featured on homepage")} testid={`asset-feature-toggle-${a.id}`}><Star className={`w-4 h-4 ${a.featured ? "fill-current" : ""}`} /></IconBtn>
                {a.published && <Link to={`/inventory/item/${a.id}`} target="_blank" className="w-9 h-9 grid place-items-center border border-white/15 hover:border-[var(--gold)]" title="View"><ExternalLink className="w-4 h-4" /></Link>}
                <Link to={`/admin/inventory/${a.id}`} className="w-9 h-9 grid place-items-center border border-white/15 hover:border-[var(--gold)]" title="Edit" data-testid={`asset-edit-${a.id}`}><Pencil className="w-4 h-4" /></Link>
                {user?.role === "owner" && <Confirm title="Delete this listing?" text={`"${a.title}" will be permanently removed.`} onConfirm={() => remove(a)}>
                  <button className="w-9 h-9 grid place-items-center border border-white/15 hover:border-rose-400 hover:text-rose-300" title="Delete" data-testid={`asset-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </Confirm>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const IconBtn = ({ children, onClick, title, active, disabled, testid }) => (
  <button onClick={onClick} title={title} disabled={disabled} data-testid={testid}
    className={`w-9 h-9 grid place-items-center border transition-colors disabled:opacity-30 ${active ? "border-[var(--gold)] text-gold" : "border-white/15 hover:border-[var(--gold)]"}`}>
    {children}
  </button>
);
